import React, { useState, FC, useRef } from 'react';

import {
  Box,
  Stack,
  Button,
  Modal,
  Center,
  ModalBody,
  ModalContent,
  ModalOverlay,
} from '@chakra-ui/react';

import ReactCrop, { Crop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

interface ImageCropperProps {
  file: File;
  aspectRatio: number;
  onCancel: () => void;
  onCropComplete: (croppedFile: File) => void;
}

const initialCrop: Crop = {
  unit: 'px',
  x: 0,
  y: 0,
  width: 100,
  height: 100,
};

const ImageCropper: FC<ImageCropperProps> = ({
  file,
  aspectRatio,
  onCancel,
  onCropComplete,
}) => {
  const imageRef = useRef<HTMLImageElement>(null);
  const imageSrc = useRef(URL.createObjectURL(file)).current;

  const [crop, setCrop] = useState<Crop>(() => {
    if (aspectRatio === 1) {
      return initialCrop;
    }

    const width = (aspectRatio * initialCrop.height) / 2;
    const height = initialCrop.height / 2;

    return {
      ...initialCrop,
      height,
      width,
    };
  });
  const [isComplete, setIsComplete] = useState(false);

  const onCropCompleteHandler = (crop: Crop) => {
    if (crop.width && crop.height && imageRef.current) {
      getCroppedImage(imageRef.current, crop);
    }
  };

  const getCroppedImage = (image: HTMLImageElement, crop: Crop) => {
    // const canvas = document.createElement('canvas');
    // const scaleX = image.naturalWidth / image.width;
    // const scaleY = image.naturalHeight / image.height;
    // canvas.width = Math.floor(crop.width * scaleX);
    // canvas.height = Math.floor(crop.height * scaleY);
    // const ctx = canvas.getContext('2d');

    // if (ctx) {
    //   ctx.imageSmoothingQuality = 'high';
    //   ctx.imageSmoothingEnabled = true;
    // }

    // ctx?.save();

    // ctx?.drawImage(
    //   image,
    //   crop.x * scaleX,
    //   crop.y * scaleY,
    //   crop.width * scaleX,
    //   crop.height * scaleY,
    //   0,
    //   0,
    //   crop.width * scaleX,
    //   crop.height * scaleY,
    // );

    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    canvas.width = crop.width || 0;
    canvas.height = crop.height || 0;

    const ctx = canvas.getContext('2d');

    if (ctx) {
      ctx.drawImage(
        image,
        (crop.x || 0) * scaleX,
        (crop.y || 0) * scaleY,
        (crop.width || 0) * scaleX,
        (crop.height || 0) * scaleY,
        0,
        0,
        crop.width || 0,
        crop.height || 0,
      );
    }

    ctx?.restore();

    canvas.toBlob(
      (blob) => {
        if (blob) {
          const fileName = file.name;

          const _file = new File([blob], fileName || '', {
            type: blob.type,
            lastModified: new Date().getMilliseconds(),
          });

          onCropComplete(_file);
        }
      },
      file.type,
      1,
    );
  };

  const isDisabled = crop.height === 0 || crop.width === 0;

  return (
    <Modal size="3xl" isCentered isOpen onClose={() => {}}>
      <ModalOverlay />
      <ModalContent>
        <ModalBody>
          <Center>
            <Box zIndex={2} p="20px">
              <ReactCrop
                crop={crop}
                ruleOfThirds
                aspect={aspectRatio}
                onChange={(newCrop) => setCrop(newCrop)}
                onComplete={() => setIsComplete(true)}>
                <img ref={imageRef} src={imageSrc} alt="img" />
              </ReactCrop>
            </Box>
          </Center>
          <Stack mb="20px" align="center">
            <Button
              w="150px"
              isDisabled={!isComplete || isDisabled}
              onClick={() => onCropCompleteHandler(crop)}>
              CROP
            </Button>
            <Button w="150px" variant="outline" onClick={onCancel}>
              CANCEL
            </Button>
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ImageCropper;
